import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from "@mui/material/Box"

import { IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import CustomHeaderGroup from "@plannel/components/aggrid/CustomHeaderGroup";
import ManageTrendAllowanceModal from "./ManageTrendAllowanceModal";
import { BulkValueUpdateButton } from "@plannel/components/ActionIconButton";
// import CustomTooltip from "@plannel/components/aggrid/CustomTooltip";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import NumberUtil from "@plannel/utils/number-util";

import targetInvEvalService from "@plannel/services/ip/target-inv-eval-service";
import planConfigService from "@plannel/services/data/plan-config-service";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, size, replace, cloneDeep, isNull, isUndefined } from "lodash";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";

const EVALUATION_DETAIL = "EVALUATION_DETAIL";
const { snackBarMessageColor } = ColorUtil;
const TargetInventoryEvaluationResult = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const EXCEL_EXPORT = "excelExport";
  const riskStatusCodes = ["excess", "stockout", "obsolescence"];
  const [strgEvalRst, setStrgEvalRst] = useState([]);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [riskCode, setRiskCode] = useState(reduxViewState?.riskCode ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [modalOpen, setModalOpen] = useState(false);

  const gridRef = useRef();
  const columnDefs = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);

  const currencyMap = useRef({});
  const userData = JSON.parse(localStorage.getItem("user"));
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultMonths = 3;
  const evalMonths = useRef(defaultMonths);
  const stockoutValueGetter = (params) => {
    return params?.data?.stockoutFlg ? GridUtils.gridValueL10N("exists") : "";
  }

  const valueFormatter = (data) => {
    return GridUtils.gridValueL10N(data.value);
  }

  const formatTrendAllowance = (param, isExport = false) => {
    const value = param?.data?.[param?.colDef?.field];
    if (isExport) {
      const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
      return replace(formatValue, /,/g, "");
    }
    return value ? `${(value * 100).toFixed(0)}%` : null;
  }
  formatTrendAllowance.customType = EXCEL_EXPORT;

  const cellStyle = (data) => {
    const styles = { backgroundColor: '#FAF4C0' };
    if (data.value < 0) {
      styles.color = 'red';
    }

    return styles;
  }


  const currencyRenderer = (param, isExport = false) => {
    const locCd = param?.data?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;


  const onColumnVisible = () => {
    const updatedVisibleColumns = GridUtils.getVisibleColumns(gridRef);
    setVisibleColumns(cloneDeep(updatedVisibleColumns));
  };

  const getColumnDefs = () => [
    { headerName: "locCd", field: "locCd", cellClass: 'stringType', width: 120, type: ["nonEditableColumn"], hide: true, pinned: "left", lockPinned: true },
    { headerName: "locDesc", field: "locDesc", width: 100, type: ["nonEditableColumn"], pinned: "left", lockPinned: true, hide: true, sortable: false },
    { headerName: "location", field: "locName", width: 100, type: ["nonEditableColumn"], pinned: "left", lockPinned: true },
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType', width: 120, type: ["nonEditableColumn"], pinned: "left", lockPinned: true },
    { headerName: "itemName", field: "itemName", width: 200, type: ["nonEditableColumn"], pinned: "left", lockPinned: true },
    { headerName: "category", field: "categoryName", width: 140, type: ["nonEditableColumn"] },
    {
      headerName: "classEval", groupId: "class",
      headerGroupComponent: CustomHeaderGroup,
      headerGroupComponentParams: {
        onExpandStateChange: onColumnVisible,
      },
      children: [
        { headerName: "invClassCd", field: "invClassCd", minWidth: 60, width: 150, type: ["nonEditableColumn"], cellRenderer: AbcXyzChip },
        {
          headerName: "domain",
          columnGroupShow: "open",
          field: "classifiDimCd",
          minWidth: 110,
          width: 150,
          type: ["nonEditableColumn"],
          valueFormatter
        },
        {
          headerName: "evalStartDt",
          columnGroupShow: "open",
          field: "evalStartDt",
          minWidth: 70,
          width: 115,
          type: ["nonEditableColumn"],
          valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM")
        },
        {
          headerName: "evalEndDt",
          columnGroupShow: "open",
          field: "evalEndDt",
          minWidth: 70,
          width: 115,
          type: ["nonEditableColumn"],
          valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM")
        },
        { headerName: "base", columnGroupShow: "open", field: "classifiBaseCd", minWidth: 60, width: 100, valueFormatter, type: ["nonEditableColumn"] }
      ]
    },
    {
      headerName: "targetStock", groupId: "targetStock",
      headerGroupComponent: CustomHeaderGroup,
      headerGroupComponentParams: {
        onExpandStateChange: onColumnVisible,
      },
      children: [
        {
          headerName: "targetInvQty",
          field: "targetInvQty",
          minWidth: 80,
          width: 135,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        },
        {
          headerName: "cycleInvQty",
          columnGroupShow: "open",
          field: "cycleInvQty",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        },
        {
          headerName: "safetyStockQty",
          columnGroupShow: "open",
          field: "safetyStockQty",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        },
        {
          headerName: "targetInvAmt",
          columnGroupShow: "open",
          field: "targetInvAmt",
          minWidth: 80,
          width: 120,
          valueFormatter: currencyRenderer,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        { headerName: "invPolicy", columnGroupShow: "open", field: "invPolicyDesc", minWidth: 100, width: 140, type: ["nonEditableColumn"] },
        {
          headerName: "otdLt",
          columnGroupShow: "open",
          field: "avgLtDays",
          minWidth: 50,
          width: 90,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: "salesQty",
          columnGroupShow: "open",
          field: "salesQty",
          minWidth: 60,
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: "salesAmt",
          columnGroupShow: "open",
          field: "salesAmt",
          minWidth: 60,
          width: 100,
          valueFormatter: currencyRenderer,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: "salesQtyAvg",
          columnGroupShow: "open",
          field: "salesQtyAvg",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        },
        {
          headerName: "salesQtyStd",
          columnGroupShow: "open",
          field: "salesQtyStd",
          minWidth: 100,
          width: 140,
          type: ["rationalColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: "orderFreq",
          columnGroupShow: "open",
          field: "rpFreq",
          minWidth: 100,
          width: 140,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
      ]
    },
    {
      headerName: "onhandVsTarget",
      children: [
        { headerName: "onhandQty", field: "currentInvQty", width: 100, type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellStyle },
        { headerName: "qtyGapFromTarget", field: "invQtyGap", width: 132, type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellStyle },
        { headerName: "onhandAmt", field: "currentInvAmt", width: 100, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, cellStyle },
        { headerName: "amtGapFromTarget", field: "invAmtGap", width: 132, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer, cellStyle },
        { headerName: "avgStockMonthly", field: "avgInvQty", width: 140, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
        { headerName: "avgStockMonthlyAmt", field: "avgInvAmt", width: 140, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer },
        ]
    },
    {
      headerName: "latestDemandSupply",
      children: [
        // { 
        //   headerName: "analysisPeriodSalesQtyAvg",
        //   field: "lastSalesNmQtyAvg", 
        //   colId: GridUtils.IGNORE_P13N_COLLID, 
        //   width: 125, 
        //   type: ["numberColumn", "nonEditableColumn", "rightAligned"] 
        // },
        { headerName: "lastSalesMQty", field: "lastSalesMQty", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
        // {
        //   headerName: "analysisPeriodSalesStd",
        //   field: "lastSalesNmStd",
        //   colId: GridUtils.IGNORE_P13N_COLLID,
        //   width: 135,
        //   type: ["rationalColumn", "nonEditableColumn", "rightAligned"]
        // },
        // {
        //   headerName: "lastAvgMLtDays",
        //   field: "lastAvgMLtDays",
        //   width: 80,
        //   type: ["rationalColumn", "nonEditableColumn", "rightAligned"]
        // },
        { headerName: "stockout", field: "stockoutFlg", width: 120, type: ["nonEditableColumn", "booleanColumn"], valueGetter: stockoutValueGetter },
      ]
    },
    {
      headerName: "inventoryTrend",
      children: [
        { headerName: "salesTrend", field: "trendDemandCd", width: 124, type: ["nonEditableColumn"], valueFormatter: (params) => GridUtils.gridValueL10N(params.value?.toLowerCase()) },
        // FIXME: 2025-06-20 hide 처리 사용성이 없고 로직 변경시 사용하지 않을 컬럼들임. 하지만 해당 measure로 다른것들을 사용 할 가능성이 존재하여 Service 로직은 아직 변경하지 않음.
        // FIXME: 추후 Back Servie 로직이 변경될때 완전히 삭제해야함.
        // {
        //   headerName: "newTargetInvQty",
        //   field: "newTargetInvQty",
        //   width: 120,
        //   type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        // },
        // {
        //   headerName: "newCycleInvQty",
        //   field: "newCycleInvQty",
        //   width: 120,
        //   type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        // },
        // {
        //   headerName: "newSafetyStockQty",
        //   field: "newSafetyStockQty",
        //   width: 120,
        //   type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        // },
        // {
        //   headerName: "targetGap",
        //   field: "newTargetQtyGap",
        //   width: 120,
        //   type: ["numberColumn", "nonEditableColumn", "rightAligned"], 
        //   headerTooltip: `${t("newTargetInvQty")} - ${t("targetStock")}`, tooltipComponent: CustomTooltip
        // },
        { headerName: "risk", field: "riskStatusCd", width: 110, type: ["nonEditableColumn"], valueFormatter: (params) => GridUtils.gridValueL10N(params.value?.toLowerCase()) },
      ]
    },
    {
      headerName: "trendAllowance", groupId: "trendAllowance",
      headerGroupComponent: CustomHeaderGroup,
      headerGroupComponentParams: {
        onExpandStateChange: onColumnVisible,
      },
      children: [
        {
          headerName: "demandRising",
          field: "allownceRatioUptrend",
          minWidth: 110,
          width: 160,
          type: ["nonEditableColumn", "rightAligned"],
          valueFormatter: formatTrendAllowance
        },
        {
          headerName: "demandShrinking",
          columnGroupShow: "open",
          field: "allownceRatioDntrend",
          minWidth: 60,
          width: 120,
          type: ["nonEditableColumn", "rightAligned"],
          valueFormatter: formatTrendAllowance
        },
        {
          headerName: "stockoutInventory",
          columnGroupShow: "open",
          field: "allownceRatioStockout",
          minWidth: 60,
          width: 120,
          type: ["nonEditableColumn", "rightAligned"],
          valueFormatter: formatTrendAllowance
        },
        {
          headerName: "excessInventory",
          columnGroupShow: "open",
          field: "allownceRatioExcess",
          minWidth: 60,
          width: 120,
          type: ["nonEditableColumn", "rightAligned"],
          valueFormatter: formatTrendAllowance
        },
        {
          headerName: "obsolescence",
          columnGroupShow: "open",
          field: "allownceRatioObsolesce",
          minWidth: 90,
          width: 120,
          type: ["nonEditableColumn", "rightAligned"],
          valueFormatter: formatTrendAllowance
        }
      ]
    }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category.leafFlg).map((category) => category.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (riskCode) {
      params.riskStatusCd = riskCode;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    return { ...params, ...sortParams };
  };

  const setEvaluationMonths = () => {
    planConfigService.getConfigCd({ moduleCd: CodeUtil.module.IP, configCd: EVALUATION_DETAIL })
    .then(response => {
      const { numVal } = response.data;
      if (numVal) {
        evalMonths.current = numVal;
      }
    })
    .catch(e => {
      debug(e);
    })
  }

  const retrieveStrgEvalRst = () => {
    const params = getRequestParams();
    targetInvEvalService.getAll(params)
      .then(response => {
        const { results, totalPages, currency } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setStrgEvalRst(results);

        if (size(currency)) {
          currencyMap.current = currency;
        }

        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, riskCode, currentPage, pageSize });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = { ...pageState, ...filterState };
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })

  }

  const onGridReady = params => {
    setGridLoading(true);
    columnDefs.current = getColumnDefs();
    GridUtils.setColumnDefs({ ...params, columnDefs: columnDefs.current, viewName, initState: true });
    setVisibleColumns(cloneDeep(GridUtils.getVisibleColumns(gridRef)));
    setEvaluationMonths();
  }

  const retrieveExportColumns = () => {
    if (isEmpty(visibleColumns)) {
      return columnDefs;
    }
    
    return visibleColumns;
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveStrgEvalRst();
    }
  }

  useEffect(() => {
    if (gridLoading) {
      retrieveStrgEvalRst();
    }

    return () => {
      setStrgEvalRst([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, currentPage, pageSize, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setStrgEvalRst([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, riskCode]);

  return (
    <>
      <FilterContainer>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
        <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} addLocType={[CodeUtil.locationType.PLANT.cd]}/>
        <Autocomplete
          key={"riskCd"}
          name={"riskCd"}
          options={riskStatusCodes}
          getOptionLabel={option => t(option)}
          value={riskCode}
          onChange={(_, newValue) => setRiskCode(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 240 }}
          renderInput={(params) => (
            <TextField {...params} label={t("risk")} />
          )}
        />
        <IPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={targetInvEvalService} columnDefs={retrieveExportColumns()} params={getRequestParams(false)} />
          <BulkValueUpdateButton label="mgmtTrendAllowance" onClick={() => setModalOpen(true)} />
        </Box>
      </FilterContainer>
      <Box id="target-inventory-evaluation-result" className="ag-theme-balham grid-area" mb={1}>
        <AgGridReact
          ref={gridRef}
          rowData={strgEvalRst}
          onColumnVisible={onColumnVisible}
          onColumnMoved={onColumnVisible}
          {...defaultGridMemo}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          onGridReady={onGridReady}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
        />
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <ManageTrendAllowanceModal
        t={t}
        evalMonths={evalMonths.current}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        setSnackInfo={setSnackInfo}
        retrieve={retrieveStrgEvalRst}
      />
    </>
  );
};

export default withTranslation()(TargetInventoryEvaluationResult);